import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import gql from 'graphql-tag';
import { useSubscription } from '@apollo/react-hooks';

import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql-error-processor';
import { getMonitoringClient, setContentErrorAttributes } from '@confluence/monitoring';
import { useSessionData, AccessStatus } from '@confluence/session-data';

import { getAGGClient } from './getAGGClient';

import { useSubscriptionValues } from './links/AGGWebSocketLink/useSubscriptionValues';

type ContentPropertyUpdated = {
	key: string;
	value: string;
	version: number;
	__typename: string;
};

type ContentPropertyDeleted = {
	key: string;
};

type ConfluenceOnContentModified = {
	type: string;
	id: string;
	__typename: string;
	_deltas: string[];
	emojiTitleUpdated: ContentPropertyUpdated;
	contentStateUpdated: ContentPropertyUpdated;
	pageTitlePropertyUpdated: ContentPropertyUpdated;
	coverPictureUpdated: ContentPropertyUpdated;
	coverPictureDeleted: ContentPropertyDeleted;
	emojiTitleDeleted: ContentPropertyDeleted;
	contentStateDeleted: ContentPropertyDeleted;
};

type SubscriptionsContextType = {
	contentModified: ConfluenceOnContentModified | null;
	id: string | null;
	setContentType: (contentType: string | null) => void;
	setContentId: (contentId: string | null) => void;
	addSubscriber: (subscriberId: string) => void;
	removeSubscriber: (subscriberId: string) => void;
};

const SubscriptionsContext = createContext<SubscriptionsContextType | undefined>(undefined);
export const errorStringsToIgnore = ['401', 'Failed to fetch', 'Load Failed', 'Network error'];

export const SubscriptionsContextProvider = ({ children }: { children: ReactNode }) => {
	const [contentModified, setContentModified] = useState<ConfluenceOnContentModified | null>(null);
	const [contentId, setContentId] = useState<string | null>(null);
	const [contentType, setContentType] = useState<string | null>(null);
	const subscribersRef = React.useRef<Record<string, number>>({}); // to keep track of subscribers
	// if there are no subscribers left we set the contentId and contentType to null
	// remove subscriber on unmount of subscription component

	const addSubscriber = useCallback((subscriberId: string) => {
		const subscribers = subscribersRef.current;
		if (subscribers.hasOwnProperty(subscriberId)) {
			subscribers[subscriberId] = subscribers[subscriberId] + 1;
		} else {
			subscribers[subscriberId] = 1;
		}
	}, []);

	const removeSubscriber = useCallback(
		(subscriberId: string) => {
			const subscribers = subscribersRef.current;
			if (subscribers.hasOwnProperty(subscriberId)) {
				subscribers[subscriberId]--;
				if (subscribers[subscriberId] <= 0) {
					delete subscribers[subscriberId];
					if (Object.keys(subscribers).length === 0) {
						setContentId(null);
						setContentType(null);
					}
				}
			}
		},
		[setContentId, setContentType],
	);

	const { id, onSubscriptionComplete, extensions, onSubscriptionData } = useSubscriptionValues({
		contentType,
		contentId,
	});

	const { accessStatus } = useSessionData();
	const skipSubscriptionForAnonUsers = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	const { error } = useSubscription(
		gql`
			subscription SubscriptionsContextProviderSubscription($id: ID!) {
				confluence_onContentModified(id: $id) {
					type
					id
					__typename
					_deltas
					contentStateUpdated {
						key
						value
						version
						__typename
					}
					emojiTitleUpdated {
						key
						value
						version
						__typename
					}
					coverPictureUpdated {
						key
						value
						version
						__typename
					}
					pageTitlePropertyUpdated {
						key
						value
						version
						__typename
					}
					coverPictureDeleted {
						key
						__typename
					}
					emojiTitleDeleted {
						key
						__typename
					}
					contentStateDeleted {
						key
						__typename
					}
				}
			}
		`,
		{
			variables: {
				id,
				__extensions: extensions,
			},
			onSubscriptionComplete,
			onSubscriptionData(response) {
				onSubscriptionData(response);
				const { subscriptionData } = response;
				if (!subscriptionData || !subscriptionData.data) {
					return;
				}
				setContentModified(subscriptionData.data.confluence_onContentModified ?? null);
			},
			skip:
				!id ||
				skipSubscriptionForAnonUsers ||
				!fg('confluence_enable_subscriptions-context-provider'),
			client: getAGGClient(),
		},
	);

	useEffect(() => {
		if (error) {
			const errorMessage = error?.message || (error as any)?.errors?.[0]?.message; // error object can be an array
			const statusCode = (error as any)?.errors?.[0]?.extensions?.statusCode;

			const isIgnoredError = errorStringsToIgnore.some((errorString) =>
				error?.message.includes(errorString),
			);

			if (statusCode !== 403 && !isIgnoredError) {
				const e = new Error(`Subscription Error - :${errorMessage}`);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					// hard coded as the package will cause circular dependency
					attribution: 'page-experiences',
				});
			}
			markErrorAsHandled(error);
		}
	}, [error, contentId, contentType]);

	const contextValue = React.useMemo(
		() => ({ contentModified, id, setContentType, setContentId, addSubscriber, removeSubscriber }),
		[contentModified, id, setContentType, setContentId, addSubscriber, removeSubscriber],
	);

	if (fg('confluence_enable_subscriptions-context-provider'))
		return (
			<SubscriptionsContext.Provider value={contextValue}>{children}</SubscriptionsContext.Provider>
		);
	else {
		return <>{children}</>;
	}
};

export const useSubscriptionsContext = () => {
	const context = useContext(SubscriptionsContext);

	return context;
};
